import { observable } from "mobx";
import { ArrayMinSize, IsNotEmpty, ValidateNested } from "class-validator";
import { defaultTo } from "lodash";
import {
  AccommodationRequestBookingGroupDataFragment,
  AccommodationTypeOf,
  FullAccommodationRequestFragment,
  HospitalityPaidBy
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { createObjectID } from "@bonliva-crm/common";
import { plainToClass } from "class-transformer";
import { FileUploadFieldValue } from "./FileUploadField";
import { AccommodationPriceType, TicketDifficultyLevel } from "@bonliva-crm/shared";

export class AccommodationRequest {
  static CreateNew(consultantName: string, customerAddress: string) {
    const item = new AccommodationRequest();
    item.customerAddress = customerAddress;
    return item;
  }

  static Create(
    data?: AccommodationRequest,
    bookingGroupData?: AccommodationRequestBookingGroupDataFragment
  ) {
    const item = data
      ? plainToClass(AccommodationRequest, data, {
          excludePrefixes: ["_"]
        })
      : new AccommodationRequest();

    item.id = data?.id || item.id;
    // item.weeks = bookingGroupData?.Weeks ? bookingGroupData?.Weeks : [];
    item.weeks = [];

    return item;
  }

  static FromFull(data: FullAccommodationRequestFragment): AccommodationRequest {
    const item = new AccommodationRequest();

    item.id = data._id;
    // item.deadline = defaultTo(data.deadline, null);
    item.city = defaultTo(data.city, null);
    item.customerAddress = defaultTo(data.customerAddress, "");
    item.budget = defaultTo(data.budget, null);
    item.budgetPeriod = defaultTo(data.budgetPeriod, AccommodationPriceType.PerNight);
    item.paidBy = defaultTo(data.paidBy, null);
    item.carAccess = defaultTo(data.carAccess, null);
    item.hasOwnCar = data.hasOwnCar ? data.hasOwnCar.toString() : null;
    item.hasCarAccess = data.hasCarAccess ? data.hasCarAccess.toString() : "false";
    item.walkingDistance = defaultTo(data.walkingDistance, "");
    item.accommodationPreferences = defaultTo(data.accommodationPreferences, "");
    item.accommodationType = defaultTo(data.accommodationType, []);
    item.sharedKitchen = defaultTo(data.sharedKitchen, false);
    item.sharedBathroom = defaultTo(data.sharedBathroom, false);
    item.ticketDifficultyLevel = defaultTo(data.ticketDifficultyLevel, null);
    item.sharedConveniences = defaultTo(data.sharedConveniences, "");

    item.additionalInfo = defaultTo(data.additionalInfo, "");
    item.shouldBeBookedDirectly = defaultTo(data.shouldBeBookedDirectly, "");
    item.periods = defaultTo(data.periods, []);
    item.periodInput = defaultTo(data.periodInput, "");
    item.accommodationSuggestion = defaultTo(data.accommodationSuggestion, null);
    item.zzNumber = defaultTo(data.zzNumber, null);

    item.differentCity = defaultTo(data.differentCity, false);
    item.specifyCity = defaultTo(data.specifyCity, "");
    item.acceptedOveragePercent = defaultTo(data.acceptedOveragePercent, null);
    item.maxAllowedPerNight = defaultTo(data.maxAllowedPerNight, null);
    item.hasAccommodationSuggestion = defaultTo(data.hasAccommodationSuggestion, false);
    item.staffAccommodation = defaultTo(data.staffAccommodation, "");
    item.accommodationLink = defaultTo(data.accommodationLink, "");
    item.bookingInformation = defaultTo(data.bookingInformation, "");
    item.parkingMaxPrice = defaultTo(data.parkingMaxPrice, null);
    item.commentAboutBooking = defaultTo(data.commentAboutBooking, "");
    item.consultantId = defaultTo(data.consultantId, null);
    item.costCenterId = defaultTo(data.costCenterId, null);
    item.projectId = defaultTo(data.projectId, null);
    item.bookingConfirmationFile = defaultTo(data.bookingConfirmationFile, null);
    item.isProlongation = defaultTo(data.isProlongation, false);
    item.isReschedule = defaultTo(data.isReschedule, false);
    item.rescheduleComment = defaultTo(data.rescheduleComment, "");

    return item;
  }

  id = createObjectID();

  @observable
  consultantId: string | null = null;

  @observable
  costCenterId: string | null = null;

  @observable
  projectId: string | null = null;

  @ArrayMinSize(1)
  @observable
  @ValidateNested({ each: true })
  periods: Array<AccommodationData> = [new AccommodationData()];

  // @observable
  // deadline: Date | null = null;

  @IsNotEmpty({ message: locale.accommodationRequest.city })
  @observable
  city: string = "";

  @observable
  customerAddress: string = "";

  @observable
  differentCity: boolean = false;

  @observable
  specifyCity: string = "";

  @IsNotEmpty({ message: locale.accommodationRequest.budget })
  @observable
  budget: string | null = null;

  @observable
  budgetPeriod: AccommodationPriceType = AccommodationPriceType.PerNight;

  @observable
  acceptedOveragePercent: string | null = null;

  @observable
  maxAllowedPerNight?: string | null = null;

  @IsNotEmpty({ message: locale.accommodationRequest.paidBy })
  @observable
  paidBy: HospitalityPaidBy | null = null;

  @IsNotEmpty({ message: locale.accommodationRequest.shouldBeBookedDirectly })
  @observable
  accommodationSuggestion: AccommodationTypeOf = null;

  @observable
  hasAccommodationSuggestion: boolean = false;

  @observable
  staffAccommodation: string | null = null;

  @observable
  bookingConfirmationFile: FileUploadFieldValue | null = null;

  @observable
  accommodationLink: string = "";

  @observable
  bookingInformation: string = "";

  @observable
  periodInput: string = "";

  @observable
  accommodationPreferences: string = "";

  @observable
  carAccess: string | null = null;

  @observable
  hasOwnCar: string | null = null;

  @observable
  hasCarAccess: string | null = null;

  @observable
  parkingMaxPrice: string | null = null;

  @observable
  walkingDistance: string = "";

  @ArrayMinSize(1)
  @IsNotEmpty()
  @observable
  accommodationType = [];

  // Split shared conveniences into two separate fields
  @observable
  sharedKitchen: boolean = false;

  @observable
  sharedConveniences: string = "";

  @observable
  sharedBathroom: boolean = false;

  @observable
  ticketDifficultyLevel: TicketDifficultyLevel | null = null;

  @observable
  additionalInfo: string = "";

  @observable
  shouldBeBookedDirectly: string = "";

  @observable
  zzNumber: string | null = null;

  @observable
  weeks: Date[] = [];

  @observable
  repeat: string = "custom";

  @observable
  weekDays: number[] = [];

  // Comment about booking - repositioned field
  @observable
  commentAboutBooking: string = "";

  @observable
  isProlongation: boolean = false;

  @observable
  isReschedule: boolean = false;

  @observable
  rescheduleComment: string = "";
}

export class AccommodationData {
  @IsNotEmpty({ message: locale.travelRequest.checkIn })
  @observable
  start?: Date;

  @IsNotEmpty({ message: locale.travelRequest.checkOut })
  @observable
  end?: Date;
}
